/**
 * Album Detail Page
 *
 * Shows album info and track list.
 * Responsive header with parallax effect.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { usePlayerStore } from '@/stores/player';
import { PlayIcon, ShuffleIcon, HeartOutlineIcon } from '@/components/icons';
import { MediaCard } from '@/components/common/MediaCard';
import { LoadingGrid } from '@/components/common/LoadingGrid';

export function AlbumPage() {
  const { id } = useParams<{ id: string }>();
  const [album, setAlbum] = useState<any>(null);
  const [songs, setSongs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { play } = usePlayerStore();

  useEffect(() => {
    async function loadAlbum() {
      if (!id) return;

      try {
        const response = await api.getAlbum(id);
        if (response.data) {
          setAlbum(response.data.album);
          setSongs(response.data.songs as any[]);
        }
      } catch (error) {
        console.error('Failed to load album:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadAlbum();
  }, [id]);

  const handlePlayAll = () => {
    if (songs.length > 0) {
      play(songs[0], songs);
    }
  };

  const handleShuffle = () => {
    if (songs.length > 0) {
      const shuffled = [...songs].sort(() => Math.random() - 0.5);
      play(shuffled[0], shuffled);
    }
  };

  if (isLoading) {
    return <LoadingGrid />;
  }

  if (!album) {
    return (
      <div className="text-center py-12">
        <p className="text-on-surface-variant">Album not found</p>
      </div>
    );
  }

  // Support both thumbnail array and direct string formats
  const thumbnail = album.thumbnail || album.thumbnails?.[album.thumbnails.length - 1]?.url;

  return (
    <div className="min-h-full">
      {/* Hero header */}
      <header className="relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 to-black" />

        <div className="relative px-4 pt-8 pb-6 flex flex-col sm:flex-row items-center gap-6">
          {/* Album art */}
          <img
            src={thumbnail || './pwa-192x192.webp'}
            alt={album.title}
            className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 rounded-xl shadow-2xl object-cover shrink-0"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={(e) => {
              (e.target as HTMLImageElement).src = './pwa-192x192.webp';
            }}
          />

          {/* Album info */}
          <div className="text-center sm:text-left flex-1">
            <p className="text-sm text-on-surface-variant uppercase tracking-wider mb-1">
              Album
            </p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
              {album.title}
            </h1>
            <p className="text-on-surface-variant mb-4">
              {album.artists?.map((a: any) => a.name).join(', ')}
              {album.year && ` • ${album.year}`}
              {songs.length > 0 && ` • ${songs.length} songs`}
            </p>

            {/* Action buttons */}
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <button className="btn-primary flex items-center gap-2" onClick={handlePlayAll}>
                <PlayIcon className="w-5 h-5" />
                Play
              </button>
              <button className="btn-secondary flex items-center gap-2" onClick={handleShuffle}>
                <ShuffleIcon className="w-5 h-5" />
                Shuffle
              </button>
              <button className="btn-icon">
                <HeartOutlineIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Track list */}
      <div className="px-4 pb-8">
        <div className="space-y-1">
          {songs.map((song, index) => (
            <button
              key={song.id}
              onClick={() => play(song, songs)}
              className="w-full list-item"
            >
              <span className="w-8 text-center text-on-surface-variant text-sm">
                {index + 1}
              </span>
              <div className="flex-1 text-left">
                <p className="font-medium line-clamp-1">{song.title}</p>
                <p className="text-sm text-on-surface-variant line-clamp-1">
                  {song.artists?.map((a: any) => a.name).join(', ')}
                </p>
              </div>
              {song.duration && (
                <span className="text-sm text-on-surface-variant">
                  {Math.floor(song.duration / 60)}:{String(song.duration % 60).padStart(2, '0')}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
